import type { AppEntry } from "./project.js";

// =============================================================================
// MARK: Store Credential Validation Checks (A7Y-446)
// =============================================================================

/**
 * Outcome of a single credential capability check.
 * - `pass`: the check ran and the credential has the required capability.
 * - `fail`: the check ran and the credential lacks the required capability.
 *   This DOES set permission_error on the store link at migrate time.
 * - `skipped`: the check did not run (e.g., GPC `listing_write` when the app
 *   has no prior APK). May carry a non-blocking message in `error.message`.
 *   `skipped` NEVER sets permission_error and never blocks Continue.
 */
export type StoreCredentialCheckStatus = "pass" | "fail" | "skipped";

/** Error detail for a failed credential check. */
export interface StoreCredentialCheckError {
  /** HTTP status when relevant; 0 for non-HTTP failures (unavailable, skipped). */
  status: number;
  /** Short user-facing message. */
  message: string;
  /** Raw detail for debugging (URL, body, etc.). */
  detail?: string;
}

/** Result for a single credential capability check. */
export interface StoreCredentialCheckResult {
  status: StoreCredentialCheckStatus;
  /** Role required to pass this check (e.g., "App Manager or Admin", "Sales, Finance, or Admin", "Admin"). */
  role: string;
  error?: StoreCredentialCheckError;
}

/**
 * Generic map of credential capability checks keyed by check key.
 * Shape is intentionally store-agnostic so A7Y-452 (GPC) can reuse it without forking.
 */
export type StoreCredentialChecks = Record<string, StoreCredentialCheckResult>;

/** Known ASC check keys (informational — the map is not restricted to these). */
export type AscCheckKey = "publishing" | "events" | "sales_trends" | "app_analytics";

/** Known GPC check keys (informational — the map is not restricted to these). */
export type GpcCheckKey = "listing_read" | "listing_write" | "analytics_read" | "reviews_reply";

// =============================================================================
// MARK: ASC in-app event capability degrade (A7Y-2884)
// =============================================================================

/**
 * Degrade mode for in-app event capabilities, derived from the `events`
 * (management, Marketing-or-higher) and `app_analytics` (analytics,
 * Admin-gated) check tiers.
 * - `full`: both events management and event analytics are available.
 * - `management_only`: can create/manage event drafts, but per-event
 *   analytics needs a higher-privileged (Admin) credential.
 * - `analysis_only`: cannot create/manage event drafts with this credential;
 *   Apptonomy still analyses events from public listing data.
 * - `unknown`: the `events` check has not run (checks absent, or the
 *   `events` key absent from a persisted snapshot) — never rendered as a
 *   green/working state.
 */
export type AscEventCapabilityMode =
  | "full"
  | "management_only"
  | "analysis_only"
  | "unknown";

/** Resolved in-app event capability for a store credential. */
export interface AscEventCapability {
  mode: AscEventCapabilityMode;
  /** True iff the credential can create/manage event drafts (events tier passed). */
  canManageEvents: boolean;
  /** True iff the credential can read per-event analytics (app_analytics tier passed). */
  canReadEventAnalytics: boolean;
}

/**
 * Derives the in-app event capability degrade from a `StoreCredentialChecks`
 * map. This is the single source of truth for the events/app_analytics
 * split — both a7y-functions (degrade at publish) and a7y-webapp (degrade
 * copy) must consume this rather than re-deriving the ternary themselves.
 *
 * Semantics:
 * - `checks` absent, or the `events` key absent from it → `unknown` (both
 *   flags false). A missing capability is never rendered as a passing state.
 * - `events` pass + `app_analytics` pass → `full`.
 * - `events` pass + `app_analytics` not pass → `management_only`.
 * - `events` not pass (regardless of `app_analytics`) → `analysis_only`,
 *   with `canReadEventAnalytics` following the `app_analytics` status.
 */
export function deriveAscEventCapability(
  checks: StoreCredentialChecks | undefined
): AscEventCapability {
  const eventsCheck = checks?.events;
  if (!eventsCheck) {
    return { mode: "unknown", canManageEvents: false, canReadEventAnalytics: false };
  }

  const canManageEvents = eventsCheck.status === "pass";
  const canReadEventAnalytics = checks?.app_analytics?.status === "pass";

  if (!canManageEvents) {
    return { mode: "analysis_only", canManageEvents: false, canReadEventAnalytics };
  }

  return {
    mode: canReadEventAnalytics ? "full" : "management_only",
    canManageEvents: true,
    canReadEventAnalytics,
  };
}

/**
 * Validates and normalizes a Play Console reporting bucket URI.
 *
 * Accepts whatever the user is likely to paste and extracts the bucket name:
 * - the bare bucket name (`pubsite_prod_12345`)
 * - the Cloud Storage URI, with or without an object path
 *   (`gs://pubsite_prod_rev_12345`, `gs://pubsite_prod_12345/reviews/`)
 * - a Cloud Console browser or `storage.googleapis.com` URL containing it
 *
 * The bucket must match Play Console's reporting bucket naming scheme:
 * `pubsite_prod_<digits>` or `pubsite_prod_rev_<digits>`.
 *
 * @returns `{ ok: true, bucketName }` on valid input,
 *   or `{ ok: false, error }` with a user-facing reason otherwise.
 */
export function normalizeGpcBucketUri(
  input: string
): { ok: true; bucketName: string } | { ok: false; error: string } {
  if (typeof input !== "string") {
    return { ok: false, error: "Bucket URI must be a string." };
  }
  const trimmed = input.trim();
  if (!trimmed) {
    return { ok: false, error: "Bucket URI is required." };
  }

  // The bucket is a whole path segment: preceded by start, `/` or `:` and
  // followed by end, `/`, `?` or `#`. Anything around it (scheme, host,
  // console path, object path like `/reviews/`) is ignored.
  const match = /(?:^|[/:])(pubsite_prod_(?:rev_)?\d+)(?=$|[/?#])/i.exec(trimmed);
  if (!match) {
    return {
      ok: false,
      error:
        "Bucket name must look like `pubsite_prod_<id>` or `pubsite_prod_rev_<id>`. " +
        "Copy the URI from Play Console > Download reports > Statistics.",
    };
  }
  return { ok: true, bucketName: match[1].toLowerCase() };
}

/** Result of normalizing an Apple ASC vendor number input. */
export type NormalizedAscVendorNumberResult =
  | { ok: true; value: string }
  | { ok: false; error: string };

/**
 * Validates and normalizes an Apple App Store Connect vendor number.
 *
 * Apple vendor numbers are 6–10 digit numeric strings (most are 8 digits;
 * older legacy accounts can be 6 or 7, and some newer accounts are longer).
 * We strip surrounding whitespace and reject anything that isn't pure
 * digits within the supported length window.
 *
 * The error string is user-facing — keep it concise and actionable.
 */
export function normalizeAscVendorNumber(
  input: string | null | undefined
): NormalizedAscVendorNumberResult {
  if (input == null) {
    return { ok: false, error: "Vendor number must be 6 to 10 digits." };
  }
  const trimmed = String(input).trim();
  if (trimmed.length === 0) {
    return { ok: false, error: "Vendor number must be 6 to 10 digits." };
  }
  if (!/^\d{6,10}$/.test(trimmed)) {
    return { ok: false, error: "Vendor number must be 6 to 10 digits." };
  }
  return { ok: true, value: trimmed };
}

/** Response shape for the validate-store-credential endpoint. */
export interface ValidateStoreCredentialResponse {
  /** True iff the required check (listing_read for ASC) passed. */
  success: boolean;
  app?: AppEntry;
  publisherName?: string;
  tempCredentialId?: string;
  checks?: StoreCredentialChecks;
  message?: string;
}

export type StoreId =
  | "APPSTORE"
  | "PLAYSTORE"
  | "UNKNOWN"
  | "appstore"
  | "playstore"
  | "unknown";

export interface StoreCredential {
  id: string;
  storeId: StoreId;
  projectId: string;
  createdByUserId: string;
  createdAt: string;
  updatedAt?: string;
  metadata?: Record<string, unknown>;
}

export interface AscCredential extends StoreCredential {
  issuerId: string;
  keyId: string;
  privateKey: string;
}

export type StorePlatform = "ios" | "android" | "macos";

export interface AppMetadata {
  appId: string; // Bundle ID for iOS, Package Name for Android
  platform: StorePlatform;
  locale: string;
  name: string;
  subtitle?: string; // iOS only
  promotionalText?: string;
  description: string;
  keywords?: string; // iOS only
  whatsNew?: string;
  primaryCategory?: string;
  secondaryCategory?: string;
  privacyPolicyUrl?: string;
  supportUrl?: string;
  marketingUrl?: string;
  screenshots?: Screenshot[];
  previewVideos?: PreviewVideo[];
  version?: string;
  releaseNotes?: string;
  lastModified?: Date;
}

export interface Screenshot {
  id: string;
  url: string;
  displayType: string; // e.g., "APP_IPHONE_65", "phoneScreenshots"
  position: number;
  locale: string;
  fileName?: string;
  uploadDate?: Date;
}

export interface PreviewVideo {
  id: string;
  url: string;
  previewFrameUrl?: string;
  displayType: string;
  position: number;
  locale: string;
  uploadDate?: Date;
}

export interface Review {
  id: string;
  platform: StorePlatform;
  appId: string;
  rating: number; // 1-5
  title?: string;
  body: string;
  reviewerName?: string;
  locale?: string;
  territory?: string;
  version?: string;
  createdDate: Date;
  modifiedDate?: Date;
  developerResponse?: DeveloperResponse;
}

export interface DeveloperResponse {
  id?: string;
  body: string;
  createdDate?: Date;
  modifiedDate?: Date;
  state?: "PUBLISHED" | "PENDING";
}

export interface ReviewQueryParams {
  limit?: number;
  offset?: number;
  sort?: "MOST_RECENT" | "MOST_HELPFUL" | "RATING_HIGH" | "RATING_LOW";
  rating?: number;
  afterDate?: Date;
  beforeDate?: Date;
  territory?: string;
  version?: string;
}

export interface AppRanking {
  appId: string;
  platform: StorePlatform;
  keyword: string;
  country: string;
  rank: number;
  category?: string;
  date: Date;
  previousRank?: number;
  change?: number;
}

export type ExperimentStatus =
  | "DRAFT"
  | "READY_TO_START"
  | "IN_PROGRESS"
  | "PAUSED"
  | "COMPLETED"
  | "STOPPED";

export type ExperimentType =
  | "ICON"
  | "SCREENSHOTS"
  | "CUSTOM_PRODUCT_PAGE"
  | "STORE_LISTING";

export interface ExperimentTreatment {
  id: string;
  name: string;
  isControl: boolean;
  metadata?: Partial<AppMetadata>;
  trafficAllocation?: number; // percentage 0-100
}

export interface ExperimentMetrics {
  impressions: number;
  pageViews: number;
  installs: number;
  conversionRate: number;
  confidenceLevel?: number;
  winner?: string; // treatment ID
  lastUpdated: Date;
}

export interface StoreExperiment {
  id: string;
  platform: StorePlatform;
  appId: string;
  name: string;
  status: ExperimentStatus;
  type: ExperimentType;
  startDate?: Date;
  endDate?: Date;
  locales: string[];
  treatments: ExperimentTreatment[];
  metrics?: ExperimentMetrics;
}

export interface AppStoreConnectCredentials {
  issuerId: string;
  keyId: string;
  privateKey: string; // PEM format
  vendorNumber?: string;
}

export interface GooglePlayCredentials {
  type: "service_account";
  project_id: string;
  private_key_id: string;
  private_key: string;
  client_email: string;
  client_id: string;
  auth_uri: string;
  token_uri: string;
  auth_provider_x509_cert_url: string;
  client_x509_cert_url: string;
}

export interface StoredCredential {
  id: string;
  organizationId: string;
  platform: StorePlatform;
  label: string;
  encryptedData: string;
  iv: string; // Initialization vector for encryption
  createdAt: Date;
  updatedAt: Date;
  lastUsed?: Date;
  expiresAt?: Date;
}

export interface CredentialRotationPolicy {
  enabled: boolean;
  rotationIntervalDays: number;
  notifyBeforeDays: number;
  autoRotate: boolean;
}

export interface StoreWebhookEvent<T = Record<string, unknown>> {
  id: string;
  platform: StorePlatform;
  eventType: string;
  appId: string;
  timestamp: Date;
  payload: T;
}
